/**
 * Renders a 1080×1080 PNG showing the user's last 30 days as a recovery
 * heatmap with summary stats. Designed for Instagram feed + Snapchat.
 *
 * Pipeline: build SVG string → convert to data URL → load into Image →
 * draw on canvas → export PNG Blob. No external dependencies.
 */

import { db } from '@/db/schema'
import { dailySeries } from '@/db/queries'

export interface HeatmapCardData {
  days: { day: string; recovery: number | null }[]
  avgRecovery: number | null
  daysInGreen: number
  totalStrain: number
  totalWorkouts: number
  dateRangeLabel: string
  firstName: string
}

/** Pull and shape the last 30 days of data into shareable form. */
export async function gatherHeatmapCardData(): Promise<HeatmapCardData> {
  const series = await dailySeries(30)
  const profile = await db.profile.get('me')

  // Pad to exactly 30 days, even if some are missing.
  const days: { day: string; recovery: number | null }[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86_400_000)
    const iso = d.toISOString().slice(0, 10)
    const match = series.find((s) => s.day === iso)
    days.push({ day: iso, recovery: match?.recovery ?? null })
  }

  const scored = days.map((d) => d.recovery).filter((v): v is number => v != null)
  const avgRecovery = scored.length
    ? scored.reduce((a, b) => a + b, 0) / scored.length
    : null
  const daysInGreen = scored.filter((r) => r >= 67).length

  // Pull workouts + cycles in this 30d window for stats.
  const since = days[0].day
  const [cycles, workouts] = await Promise.all([
    db.cycles.where('day').aboveOrEqual(since).toArray(),
    db.workouts.where('day').aboveOrEqual(since).toArray(),
  ])
  const totalStrain = cycles.reduce((sum, c) => sum + (c.score?.strain ?? 0), 0)
  const totalWorkouts = workouts.length

  const firstDate = new Date(days[0].day + 'T12:00:00')
  const lastDate = new Date(days[29].day + 'T12:00:00')
  const dateRangeLabel = `${firstDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} — ${lastDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`

  return {
    days,
    avgRecovery,
    daysInGreen,
    totalStrain,
    totalWorkouts,
    dateRangeLabel,
    firstName: profile?.first_name ?? '',
  }
}

/** Build the SVG string for the 30-day card at 1080×1080. */
export function renderHeatmapCardSVG(
  data: HeatmapCardData,
  logoDataUrl: string,
): string {
  const W = 1080
  const H = 1080
  const cardBg = '#fafaf9' // stone-50
  const inkPrimary = '#1c1917' // stone-900
  const inkMuted = '#78716c' // stone-500
  const brandOlive = '#A8AF87'
  const brandDark = '#6B7053'

  // Heatmap layout — 6 columns × 5 rows of 96px squares, 12px gap.
  const cell = 96
  const gap = 12
  const cols = 6
  const rows = 5
  const heatW = cols * cell + (cols - 1) * gap
  const heatH = rows * cell + (rows - 1) * gap
  const heatX = (W - heatW) / 2
  const heatY = 290

  const cells = data.days
    .map((d, i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      const x = heatX + col * (cell + gap)
      const y = heatY + row * (cell + gap)
      const fill = colorFor(d.recovery)
      return `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" rx="16" fill="${fill}" />`
    })
    .join('')

  const stats = [
    {
      label: 'Avg recovery',
      value: data.avgRecovery != null ? `${Math.round(data.avgRecovery)}%` : '—',
    },
    { label: 'Green days', value: `${data.daysInGreen}` },
    { label: 'Total strain', value: data.totalStrain.toFixed(0) },
    { label: 'Workouts', value: `${data.totalWorkouts}` },
  ]
  const statW = 220
  const statH = 110
  const statGap = 20
  const statTotalW = stats.length * statW + (stats.length - 1) * statGap
  const statStartX = (W - statTotalW) / 2
  const statY = heatY + heatH + 60

  const statTiles = stats
    .map((s, i) => {
      const x = statStartX + i * (statW + statGap)
      return `
        <g transform="translate(${x}, ${statY})">
          <rect width="${statW}" height="${statH}" rx="20" fill="#ffffff" stroke="#e7e5e4" />
          <text x="${statW / 2}" y="40" font-size="18" font-family="Inter, system-ui, sans-serif" fill="${inkMuted}" text-anchor="middle" letter-spacing="1.5">${s.label.toUpperCase()}</text>
          <text x="${statW / 2}" y="86" font-size="40" font-weight="600" font-family="Inter, system-ui, sans-serif" fill="${inkPrimary}" text-anchor="middle">${s.value}</text>
        </g>`
    })
    .join('')

  const footerY = statY + statH + 50

  const headline = data.firstName
    ? `${data.firstName}'s last 30 days`
    : 'Last 30 days'

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
  <rect width="${W}" height="${H}" fill="${cardBg}" />

  <!-- Header: logo + wordmark -->
  <image href="${logoDataUrl}" x="80" y="80" width="96" height="96" preserveAspectRatio="xMidYMid meet" />
  <text x="196" y="128" font-size="36" font-weight="600" font-family="Inter, system-ui, sans-serif" fill="${inkPrimary}">Lensify</text>
  <text x="196" y="166" font-size="22" font-family="Inter, system-ui, sans-serif" fill="${inkMuted}">@bijan</text>

  <!-- Hero title -->
  <text x="${W / 2}" y="240" font-size="52" font-weight="600" font-family="Inter, system-ui, sans-serif" fill="${inkPrimary}" text-anchor="middle">${escapeXml(headline)}</text>
  <text x="${W / 2}" y="278" font-size="22" font-family="Inter, system-ui, sans-serif" fill="${inkMuted}" text-anchor="middle">${escapeXml(data.dateRangeLabel)}</text>

  <!-- Heatmap -->
  ${cells}

  <!-- "30 days ago" / "today" axis labels -->
  <text x="${heatX}" y="${heatY + heatH + 38}" font-size="18" font-family="Inter, system-ui, sans-serif" fill="${inkMuted}">30 days ago</text>
  <text x="${heatX + heatW}" y="${heatY + heatH + 38}" font-size="18" font-family="Inter, system-ui, sans-serif" fill="${inkMuted}" text-anchor="end">today</text>

  <!-- Stat tiles -->
  ${statTiles}

  <!-- Footer signature -->
  <text x="${W / 2}" y="${footerY}" font-size="22" font-family="Inter, system-ui, sans-serif" fill="${brandDark}" text-anchor="middle" letter-spacing="2" font-weight="500">L E N S I F Y</text>
  <line x1="${W / 2 - 60}" y1="${footerY + 14}" x2="${W / 2 + 60}" y2="${footerY + 14}" stroke="${brandOlive}" stroke-width="2" />
</svg>`
}

function colorFor(score: number | null): string {
  if (score == null) return '#e7e5e4' // stone-200
  if (score >= 67) return '#22c55e' // green-500
  if (score >= 50) return '#4ade80' // green-400
  if (score >= 34) return '#facc15' // yellow-400
  if (score >= 20) return '#fb923c' // orange-400
  return '#f87171' // red-400
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}
